import {Button} from '@/components/ui/button/Button'
import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalOverlay,
    ModalParagraph,
    ModalTitle
} from '@/components/ui/modal-atoms/ModalAtoms'
import {useTranslation} from '@/translations/i18n'
import {PropsWithChildren, useEffect, useState} from 'react'
import {useBlocker} from 'react-router-dom'

interface UnsavedBlockerProps extends PropsWithChildren {
    isDirty: boolean
    ignoreSearchParams?: boolean
    message?: string
}

const IS_PROD = location.hostname != 'localhost' && location.hostname != '127.0.0.1'

export const UnsavedBlocker = ({isDirty, ignoreSearchParams = false, children}: UnsavedBlockerProps) => {
    const {t} = useTranslation()
    const [isModalOpen, setIsModalOpen] = useState(false)

    const blocker = useBlocker(({currentLocation, nextLocation}) => {
        if (ignoreSearchParams && currentLocation.pathname == nextLocation.pathname) {
            return false
        }

        return isDirty
    })

    // Reset the blocker if not dirty, show the modal if is dirty
    useEffect(() => {
        if (blocker.state == 'blocked' && !isDirty) {
            blocker.reset()
        }
        if (blocker.state == 'blocked' && isDirty) {
            setIsModalOpen(true)
        }
    }, [blocker, isDirty])

    // Browser unsaved blocker
    useEffect(() => {
        if (IS_PROD) {
            const handleBeforeUnload = (event: BeforeUnloadEvent) => {
                if (isDirty) {
                    event.preventDefault()
                }
            }

            window.addEventListener('beforeunload', handleBeforeUnload)
            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload)
            }
        }
    }, [isDirty, IS_PROD])

    // Modal is closed and page is changed after submit
    const onCancel = () => {
        setIsModalOpen(false)
    }

    // Modal is closed and page is changed without submitting
    const onLeave = () => {
        setIsModalOpen(false)
        blocker.proceed?.()
    }

    return (
        <>
            {isModalOpen && (
                <>
                    <ModalOverlay />
                    <Modal width="400px">
                        <ModalBody>
                            <ModalTitle>{t('commons:unsaved_blocker_title')}</ModalTitle>
                            <ModalParagraph>{t('commons:unsaved_blocker_paragraph')}</ModalParagraph>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="secondary" onClick={onCancel}>
                                {t('commons:cancel')}
                            </Button>
                            <Button variant="primaryDanger" onClick={onLeave}>
                                {t('commons:leave')}
                            </Button>
                        </ModalFooter>
                    </Modal>
                </>
            )}
            {children}
        </>
    )
}
UnsavedBlocker.displayName = 'UnsavedBlocker'
